import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

const GalleryCard = ({ gallery }) => (
  <Link
    to={gallery.slug ? `/galleries/${gallery.slug}` : `/gallery/${gallery.id}`}
    className="collections-gallery-card group/card relative block snap-start overflow-hidden rounded-sm bg-white/10"
  >
    <div className="collections-gallery-media overflow-hidden bg-black/30">
      {gallery.cover_url ? (
        <img
          src={gallery.cover_url}
          alt=""
          loading="lazy"
          className="block h-full w-full object-cover object-center transition duration-700 group-hover/card:scale-105 motion-reduce:transition-none"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-white/60">Cover coming soon</div>
      )}
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/5 to-transparent" />
    <div className="collections-gallery-caption absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 text-white">
      <h3 className="collections-gallery-title font-medium">{gallery.title}</h3>
      {gallery.has_password && (
        <span className="rounded-full bg-black/60 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em]">
          Private
        </span>
      )}
    </div>
  </Link>
);

const CategorySection = ({ category, index }) => {
  const sectionRef = useRef(null);
  const [frame, setFrame] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const images = useMemo(() => category.featured_images || [], [category.featured_images]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const updateVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.45 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !pageVisible || reduceMotion || images.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setFrame((current) => (current + 1) % images.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [images.length, isVisible, pageVisible, reduceMotion]);

  useEffect(() => {
    if (images.length > 1) {
      const preload = new Image();
      preload.src = images[(frame + 1) % images.length].url;
    }
  }, [frame, images]);

  return (
    <section
      ref={sectionRef}
      className="collections-category relative isolate flex snap-start flex-col justify-end bg-[#161718] text-white"
      aria-labelledby={`category-${category.slug}`}
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {images.length > 0 ? (
          images.map((image, imageIndex) => (
            <img
              key={image.id}
              src={image.url}
              alt=""
              aria-hidden={imageIndex !== frame}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 motion-reduce:transition-none ${
                imageIndex === frame ? "opacity-100" : "opacity-0"
              }`}
            />
          ))
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.12),transparent_38%),linear-gradient(135deg,#25292b,#0d0e0f)]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/25 to-black/95" />
      </div>

      <div className="collections-category-content relative z-10 w-full">
        <div className="collections-category-copy max-w-3xl">
          <p className="collections-category-number uppercase tracking-[0.35em] text-white/65">
            {String(index + 1).padStart(2, "0")}
          </p>
          <h2 id={`category-${category.slug}`} className="collections-category-title font-light tracking-tight">
            {category.name}
          </h2>
          <p className="collections-category-description max-w-xl text-white/75">
            {category.description}
          </p>
          <Link
            to={`/collections/${category.slug}`}
            className="collections-category-action inline-flex border-b border-white/60 pb-1 uppercase tracking-[0.2em] transition hover:border-white"
          >
            Explore {category.gallery_count ? `${category.gallery_count} galleries` : "the collection"}
          </Link>
        </div>

        {category.galleries.length > 0 ? (
          <div className="collections-gallery-strip flex snap-x overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {category.galleries.map((gallery) => (
              <GalleryCard key={gallery.id} gallery={gallery} />
            ))}
          </div>
        ) : (
          <p className="border-t border-white/20 pt-5 text-sm text-white/55">New work will be added here soon.</p>
        )}
      </div>
    </section>
  );
};

const PublicGalleries = () => {
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("loading");

  const loadCollections = async () => {
    setStatus("loading");
    try {
      const response = await axios.get(`${API_URL}/api/collections`);
      setCategories(response.data.categories || []);
      setStatus("ready");
    } catch (error) {
      console.error("Unable to load collections:", error.response?.data || error.message);
      setStatus("error");
    }
  };

  useEffect(() => {
    loadCollections();
  }, []);

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-[#111] text-white">Loading collections…</div>;
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#111] px-6 text-center text-white">
        <p>Collections could not be loaded.</p>
        <button onClick={loadCollections} className="border border-white/40 px-5 py-2 text-sm">Try again</button>
      </div>
    );
  }

  return (
    <main className="collections-scroll bg-[#111]">
      {categories.length ? (
        categories.map((category, index) => (
          <CategorySection key={category.id} category={category} index={index} />
        ))
      ) : (
        <div className="flex min-h-screen items-center justify-center px-6 text-center text-white/70">
          Collections are being curated.
        </div>
      )}
    </main>
  );
};

export default PublicGalleries;
